class CustomSewingMeasurements extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId || '');
    this.dialog = this.querySelector('dialog');
    this.openButton = this.querySelector('[data-custom-sewing-open]');
    this.saveButton = this.querySelector('[data-custom-sewing-save]');
    this.closeButtons = this.querySelectorAll('[data-custom-sewing-close]');
    this.removeButton = this.querySelector('[data-custom-sewing-remove]');
    this.savedMessage = this.querySelector('[data-custom-sewing-saved]');
    this.errorMessage = this.querySelector('[data-custom-sewing-error]');
    this.inputs = Array.from(this.querySelectorAll('[data-custom-sewing-input]'));
    this.serviceVariantId = this.dataset.serviceVariantId || '';
    this.summaryName = this.dataset.summaryName || 'Custom Sewing';
    this.summaryValue = this.dataset.summaryValue || 'Measurements provided';
    this.serviceForLabel = this.dataset.serviceForLabel || 'For';

    if (!this.form || !this.dialog || !this.openButton || !this.saveButton) return;

    this.hiddenFields = this.form.querySelector(`[data-custom-sewing-hidden-fields="${this.id}"]`);

    if (!this.hiddenFields) {
      this.hiddenFields = document.createElement('div');
      this.hiddenFields.hidden = true;
      this.hiddenFields.dataset.customSewingHiddenFields = this.id;
      this.form.append(this.hiddenFields);
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.openButton.addEventListener('click', this.openDialog, { signal });
    this.saveButton.addEventListener('click', this.saveMeasurements, { signal });
    this.removeButton?.addEventListener('click', this.removeMeasurements, { signal });
    this.dialog.addEventListener('click', this.closeOnBackdrop, { signal });
    this.dialog.addEventListener('keydown', this.handleDialogKeydown, { signal });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.closeDialog, { signal });
    });

    this.inputs.forEach((input) => {
      input.addEventListener('input', () => {
        input.setAttribute('aria-invalid', 'false');
        this.hideError();
      }, { signal });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  openDialog = () => {
    this.dialog.showModal();
    this.inputs[0]?.focus();
  };

  closeDialog = () => {
    this.dialog.close();
  };

  closeOnBackdrop = (event) => {
    if (event.target === this.dialog) this.closeDialog();
  };

  handleDialogKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    this.saveMeasurements();
  };

  saveMeasurements = () => {
    if (!this.validate()) return;

    this.hiddenFields.replaceChildren();
    this.hiddenFields.append(this.createPropertyInput(this.summaryName, this.summaryValue));

    this.inputs.forEach((input) => {
      const propertyName = input.dataset.propertyName;
      const value = this.formatMeasurement(input.value);

      if (propertyName && value) {
        this.hiddenFields.append(this.createPropertyInput(propertyName, value));
      }
    });

    this.openButton.textContent = this.openButton.dataset.editLabel || 'Edit Sewing Measurements';
    this.form.dataset.customSewingActive = 'true';
    this.form.dataset.customSewingServiceForLabel = this.serviceForLabel;
    if (this.serviceVariantId) this.form.dataset.customSewingServiceVariantId = this.serviceVariantId;
    this.savedMessage.hidden = false;
    this.closeDialog();
  };

  removeMeasurements = () => {
    this.hiddenFields.replaceChildren();
    this.inputs.forEach((input) => {
      input.value = '';
      input.setAttribute('aria-invalid', 'false');
    });

    this.openButton.textContent = this.openButton.dataset.addLabel || 'Add Sewing Measurements';
    delete this.form.dataset.customSewingActive;
    delete this.form.dataset.customSewingServiceForLabel;
    delete this.form.dataset.customSewingServiceVariantId;
    this.savedMessage.hidden = true;
    this.hideError();
  };

  validate() {
    let firstInvalidInput = null;

    this.inputs.forEach((input) => {
      const isValid = input.value.trim() !== '' && input.validity.valid && Number(input.value) > 0;
      input.setAttribute('aria-invalid', isValid ? 'false' : 'true');

      if (!isValid && !firstInvalidInput) firstInvalidInput = input;
    });

    if (firstInvalidInput) {
      this.showError();
      firstInvalidInput.focus();
      return false;
    }

    this.hideError();
    return true;
  }

  showError() {
    if (this.errorMessage) this.errorMessage.hidden = false;
  }

  hideError() {
    if (this.errorMessage) this.errorMessage.hidden = true;
  }

  createPropertyInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;

    return input;
  }

  formatMeasurement(value) {
    const measurement = Number(value);

    if (!Number.isFinite(measurement)) return '';

    return `${measurement.toString()} cm`;
  }
}

if (!customElements.get('custom-sewing-measurements')) {
  customElements.define('custom-sewing-measurements', CustomSewingMeasurements);
}
